/**
 * @file Wrapper for IndexedDB with advanced connection management
 * @copyright Generic code designed and created by Kyley Harris
 * @license This is generic utility code not unique to the AGWA project.
 * Created and maintained by Kyley Harris with usage rights granted to all projects.
 *
 * This module provides a robust wrapper around the browser's IndexedDB API with
 * advanced features like:
 * - Connection pooling
 * - Single-flight connection handling
 * - Automatic version migration
 * - Delayed connection closing for performance optimization
 * - User-friendly error notifications
 * - Built-in transaction management
 */

// Copyright - Kyley Harris (2021) - All rights to copy and reuse allowed
import * as db from './indexed-db';
import type { GetOptions } from './indexed-db';
import { alertAction } from '../components/dialogs/agwa-confirm-dialog';

/**
 * Displays an information message to the user
 *
 * @param title - The title of the message
 * @param message - The body content of the message
 */
function information(title: string, message: string) {
  void alertAction({
    title,
    message,
    confirmText: 'OK',
    variant: 'warning',
  });
}

/**
 * Advanced IndexedDB access wrapper class that provides connection pooling,
 * transaction management, and user-friendly error handling.
 *
 * This class is designed to be extended for specific database implementations.
 * Subclasses must implement:
 * - dbName(): To specify the database name
 * - getDatabaseUpgrades(): To specify database upgrade paths
 *
 * Features:
 * - Connection pooling to optimize performance for burst operations
 * - Single-flight connections to prevent race conditions
 * - Automatic error handling with user notifications
 * - Simplified CRUD operations with typed returns
 */
export class IndexedDBAccess {
  /** Active database connection, if any */
  protected _connection: IDBDatabase | null = null;

  /** Promise for an in-progress connection attempt, used for single-flight protection */
  private _connecting: Promise<IDBDatabase> | null = null;

  /** Timeout handle for the delayed connection close operation */
  private _closeTimeout: ReturnType<typeof setTimeout> | null = null;

  /** How long to keep the connection open after last use (in milliseconds) */
  private _connectionIdleTime: number = 30000; // 30 seconds in milliseconds

  /** Flag to prevent multiple blocked notifications */
  private _blockedNotified = false;

  /** Flag to prevent multiple version change notifications */
  private _versionChangeNotified = false;

  /**
   * Returns the database name for this IndexedDB instance.
   * Must be implemented by subclasses.
   *
   * @returns The name of the database
   * @throws Error if not implemented by subclass
   */
  public dbName(): string {
    throw new Error('override dbname');
  }

  /**
   * Determines the database version based on the highest version number
   * in the upgrade path.
   *
   * @returns The highest version number from the database upgrades
   */
  protected version(): number {
    let version = 1;
    this.getDatabaseUpgrades().forEach(
      (x) => (version = Math.max(version, x.version))
    );
    return version;
  }

  /**
   * Returns the list of database upgrades that define the database schema
   * and migration path. Must be implemented by subclasses.
   *
   * @returns Array of database upgrade definitions
   * @throws Error if not implemented by subclass
   */
  protected getDatabaseUpgrades(): db.DatabaseUpgrade[] {
    throw new Error('Method not implemented.');
  }

  /**
   * Immediately closes the database connection.
   * Clears any pending delayed close timeout.
   */
  protected close(): void {
    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
      this._closeTimeout = null;
    }

    if (this._connection) {
      try {
        this._connection.close();
      } catch {
        // noop
      } finally {
        this._connection = null;
      }
    }
  }

  /**
   * Schedules the connection to be closed after a period of inactivity.
   * Uses the _connectionIdleTime property to determine the delay.
   * This is part of the connection pooling strategy to optimize performance.
   */
  protected delayedClose(): void {
    // Clear any existing timeout
    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
    }

    // Set new timeout for delayed closing
    this._closeTimeout = setTimeout(() => {
      this.close();
    }, this._connectionIdleTime);
  }

  /**
   * Forces an immediate connection close.
   * Used when a version change is detected or when explicitly needed.
   */
  public forceClose(): void {
    this.close();
  }

  /**
   * Establishes a connection to the IndexedDB database.
   *
   * This method implements several optimizations:
   * 1. Connection reuse - If already connected, returns the active connection
   * 2. Single-flight protection - If a connection is in progress, waits for it rather than starting a new one
   * 3. Connection pooling - Keeps connections open for performance
   * 4. User notifications - Provides clear messages for blocked or version change scenarios
   * 5. Performance metrics - Logs connection times for monitoring
   *
   * @returns Promise resolving to an IDBDatabase connection
   */
  public async connect(): Promise<IDBDatabase> {
    const startTime = performance.now();
    let connectionType = 'new';

    try {
      if (this._connection) {
        connectionType = 'reused';
        db.log('Database connection reused');
        return this._connection;
      }
      if (this._connecting) {
        connectionType = 'shared';
        db.log('Database open already in progress; awaiting existing attempt');
        this._connection = await this._connecting;
        return this._connection;
      }

      this._blockedNotified = false;
      this._versionChangeNotified = false;

      this._connecting = db.openDb({
        dbName: this.dbName(),
        version: this.version(),
        upgrades: this.getDatabaseUpgrades(),
        onBlocked: () => {
          if (!this._blockedNotified) {
            this._blockedNotified = true;
            information(
              'Database Access Blocked',
              'Please close other tabs or windows using this application and try again.'
            );
          }
        },
      });

      const conn = await this._connecting;

      // openDb already wires some events, but we add a user-facing notification here.
      conn.onversionchange = () => {
        // Force immediate close of the connection on version change
        this.forceClose();
        db.log(
          'A new version of the database is ready, please reload this page'
        );
        if (!this._versionChangeNotified) {
          this._versionChangeNotified = true;
          information(
            'Application Update',
            'A new version is available. Please reload the page to continue.'
          );
        }
      };

      this._connection = conn;
      return conn;
    } finally {
      // ensure the single-flight flag is cleared even if open failed
      this._connecting = null;

      // Log performance metrics
      const endTime = performance.now();
      const duration = endTime - startTime;
      console.log(
        `IndexedDB connection (${connectionType}) took ${duration.toFixed(2)}ms for ${this.dbName()}`
      );
    }
  }

  /**
   * Retrieves the first item matching the provided options from the specified object store.
   *
   * @template T - The type of item to retrieve
   * @param objectStore - The name of the object store to query
   * @param options - Optional query parameters (key, index, direction, etc.)
   * @returns Promise resolving to the first matching item or null if none found
   */
  public async getFirst<T>(
    objectStore: string,
    options?: GetOptions
  ): Promise<T | null> {
    const conn = await this.connect();
    try {
      return await db.get<T>(conn, objectStore, options);
    } finally {
      this.delayedClose();
    }
  }

  /**
   * Converts a type to its corresponding store name.
   * By default, uses the name property of the type.
   *
   * @param type - An object with a name property
   * @returns The store name as a string
   */
  public store(type: { name: string }): string {
    return type.name;
  }

  /**
   * Retrieves all items matching the provided options from the specified object store.
   *
   * @template T - The type of items to retrieve
   * @param objectStore - The name of the object store to query
   * @param options - Optional query parameters (key range, index, direction, etc.)
   * @returns Promise resolving to an array of all matching items
   */
  public async getAll<T>(
    objectStore: string,
    options?: GetOptions
  ): Promise<T[]> {
    const conn = await this.connect();
    try {
      return await db.getAll<T>(conn, objectStore, options);
    } finally {
      this.delayedClose();
    }
  }

  /**
   * Stores a single item in the specified object store.
   * Will either add the item if it doesn't exist, or update it if it does.
   *
   * @template T - The type of item to store, must include an optional id property
   * @param item - The item to store
   * @param objectStore - The name of the object store
   * @returns Promise resolving to true if successful, false if an error occurred
   */
  public async put<T extends { id?: IDBValidKey }>(
    item: T,
    objectStore: string
  ): Promise<boolean> {
    const conn = await this.connect();
    try {
      await db.put<T>(conn, objectStore, item);
      return true;
    } catch {
      return false;
    } finally {
      this.delayedClose();
    }
  }

  /**
   * Stores multiple items in the specified object store in a single transaction.
   * More efficient than multiple individual put operations.
   *
   * @template T - The type of items to store, must include an optional id property
   * @param items - Array of items to store
   * @param objectStore - The name of the object store
   * @returns Promise resolving to true if successful, false if an error occurred
   */
  public async putMany<T extends { id?: IDBValidKey }>(
    items: T[],
    objectStore: string
  ): Promise<boolean> {
    const conn = await this.connect();
    try {
      await db.putAll<T>(conn, objectStore, items);
      return true;
    } catch {
      return false;
    } finally {
      this.delayedClose();
    }
  }

  /**
   * Deletes an item with the specified ID from the object store.
   *
   * @param id - The unique identifier of the item to delete
   * @param objectStore - The name of the object store
   * @returns Promise that resolves when the deletion is complete
   */
  public async del(id: IDBValidKey, objectStore: string): Promise<void> {
    const conn = await this.connect();
    try {
      await db.del(conn, objectStore, id);
    } finally {
      this.delayedClose();
    }
  }
}
